/* eslint-disable max-lines */
import escapeRegExp from 'lodash/escapeRegExp'
import trim from 'lodash/trim'
import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { AggregationPipeline } from 'types/common/aggregation'
import { S3FileTypeEnum } from 'constants/aws'
import {
  getGroupMessageThreads,
  getAllUnreadGeneralMessages,
  getCountGroupMessageThreads,
  getMessages,
  getGeneralMessageThreads,
  countAllUnreadGroupThread
} from 'API/messages'
import {
  createMessageInSupportThreads,
  createSupportMessageThreads,
  getSupportMessageThreads,
  countAllUnreadSupportThread,
  getAllUnreadSupportMessages,
  getCountSupportMessageThreads,
  getSupportMessageAggregate,
  getSupportMessages,
  getSupportMessageThreadStatusHistory,
  getSupportThread
} from 'API/messages/support-message'
import { getStepsByAggregate } from 'API/step'
import { ProcessPublishStatus } from 'components/Pages/MessagesPage/SupportMessages/components/SupportThreadFilter'
import { AuthRoleIdEnum } from 'constants/user'
import {
  IGroupMessages,
  IMessageGroup,
  IMessageGroupThreads,
  ISupportMessage,
  ISupportMessageThread,
  ISupportMessageThreadStatusHistory,
  ISupportThreadDto,
  SupportMessageThreadStatus
} from 'interfaces/message'
import { IStepWithRelations } from 'interfaces/step'
import { IUserWithRelations } from 'interfaces/user'
import last from 'lodash/last'
import { ESupportThreadSortBy, ISupportThreadFilter } from 'pages/SupportInboxPage/constants'
import { getValidArray } from 'utils/common'
import { getSupportMessageThread, getSupportMessageThreadForUnreadCount } from 'utils/message.query'
const defaultThead = {
  id: 0,
  name: '',
  status: 0,
  organizationId: 0,
  lastMessageAt: new Date(),
  supportMessages: [],
  supportMessage: {} as ISupportMessage,
  createdAt: new Date()
}
class MessageStore {
  rootStore: RootStore

  // INFO: general message
  unreadGroupThreadCount: number = 0
  messageGroupThreads: IMessageGroup[] = []
  totalThreadCount: number = 0
  isLoading: boolean = false
  generalMessages: IGroupMessages[] = []
  currentThreadId: number = 0
  generalMessagePage: number = 1
  generaltMessageKeyword: string = ''

  unreadGeneralThreadCount: number = 0
  generalMessageThreads: IMessageGroupThreads[] = []
  currentGeneralThreadId: number = 0

  // INFO: support message
  unreadSupportThreadCount: number = 0
  supportThreads: ISupportThreadDto[] = []
  totalSupportThreadCount: number = 0
  supportMessages: ISupportMessage[] = []
  currentSupportThreadId: number = 0
  selectedSupportThread: ISupportMessageThread = defaultThead
  stepOptions: IStepWithRelations[] = []
  statusHistory: ISupportMessageThreadStatusHistory[] = []
  supportThreadFilter: ISupportThreadFilter = {
    sort: ESupportThreadSortBy.UNCLAIMED_FIRST,
    status: SupportMessageThreadStatus.ALL,
    publish: ProcessPublishStatus.ALL
  }
  supportMessagePage: number = 1
  supportMessageKeyword: string = ''

  sort: ESupportThreadSortBy = ESupportThreadSortBy.NEWEST
  isUnclaimed: boolean = false
  isClaimedByMe: boolean = false
  isClaimedByOthers: boolean = false
  isResolved: boolean = false
  statusList: SupportMessageThreadStatus[] = []
  publish: ProcessPublishStatus = ProcessPublishStatus.ALL
  isPublished: boolean = false
  isDraft: boolean = false
  includeParticipants: IUserWithRelations[] = []
  modifiedDate: Date[] = []

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)

    this.rootStore = rootStore
  }

  /**
   * Clear org-scoped message caches. Called on play-profile switch (via
   * clearOrgScopedStores) so a freshly-activated profile never observes the
   * previous profile's threads/messages. Messages are scoped by
   * authStore.activeOrgId, which changes on switch — this drops the stale data
   * until the next fetch under the new scope.
   */
  reset(): void {
    // General messages
    this.unreadGroupThreadCount = 0
    this.messageGroupThreads = []
    this.totalThreadCount = 0
    this.isLoading = false
    this.generalMessages = []
    this.currentThreadId = 0
    this.generalMessagePage = 1
    this.generaltMessageKeyword = ''
    this.unreadGeneralThreadCount = 0
    this.generalMessageThreads = []
    this.currentGeneralThreadId = 0
    // Support messages
    this.unreadSupportThreadCount = 0
    this.supportThreads = []
    this.totalSupportThreadCount = 0
    this.supportMessages = []
    this.currentSupportThreadId = 0
    this.supportMessagePage = 1
    this.supportMessageKeyword = ''
    this.statusHistory = []
  }

  public async fetchGeneralMessageThreads(
    limit: number,
    keyword: string,
    isFetchFirstThread: boolean = false
  ): Promise<void> {
    this.isLoading = true
    let unreadCount = 0
    const generalMessageThreads = await getGeneralMessageThreads(limit, keyword)
    this.generalMessageThreads = getValidArray(generalMessageThreads).map(generalThread => {
      const userSeen = generalThread?.groupMessageThreadUserSeens?.find(
        seenData => seenData?.userId === this.rootStore.authStore.userDetail.id
      )
      const isSeen = userSeen?.lastSeenAt ? userSeen?.lastSeenAt >= generalThread?.lastMessageAt : false
      if (!isSeen) {
        unreadCount += 1
      }
      return {
        ...generalThread,
        isSeen
      }
    })
    // this.getUnreadThread()
    if (this.currentGeneralThreadId === 0 && generalMessageThreads.length > 0 && isFetchFirstThread) {
      this.currentGeneralThreadId = generalMessageThreads[0]?.id
    }
    this.unreadGeneralThreadCount = unreadCount
    this.isLoading = false
  }

  public async setCurrentGeneralThreadId(threadId: number): Promise<void> {
    this.currentGeneralThreadId = threadId
  }

  // TODO : write aggregate to filter on name ?
  public async getGroupMessageThreads(
    offset: number,
    limit: number,
    keyword?: string
  ): Promise<void> {
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0
    this.isLoading = true
    this.messageGroupThreads = await getGroupMessageThreads({
      order: ['lastMessageAt DESC'],
      offset,
      where: {
        organizationId,
        name: {
          like: `.*${escapeRegExp(trim(keyword))}.*`,
          options: 'i'
        }
      },
      limit,
      include: [
        {
          relation: 'groups',
          scope: {
            fields: ['name'],
            order: ['name ASC']
          }
        },
        {
          relation: 'groupMessages',
          scope: {
            order: 'createdAt ASC',
            include: [
              {
                relation: 'user'
              }
            ]
          }
        }
      ]
    })
    this.isLoading = false
  }

  public async getCountGroupMessageThreads(keyword = ''): Promise<void> {
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0
    const countResult = await getCountGroupMessageThreads({
      organizationId,
      name: {
        like: `.*${escapeRegExp(trim(keyword))}.*`,
        options: 'i'
      }
    })
    this.totalThreadCount = countResult
  }

  public async getUnreadThread(): Promise<void> {
    const pipeline = getSupportMessageThreadForUnreadCount(
      this.rootStore.authStore.activeOrgId ?? 0,
      this.rootStore.authStore.userDetail.id,
      this.rootStore.authStore.currentMembership?.roleId === AuthRoleIdEnum.PLAYER &&
        !this?.rootStore?.authStore?.userDetail?.isMessageFullAccess
    )
    
    this.unreadSupportThreadCount = await countAllUnreadSupportThread(pipeline)
    this.unreadGeneralThreadCount = await this.calculateUnreadGeneralThreadCount()
  }

  private async calculateUnreadGeneralThreadCount(): Promise<number> {
    const generalMessageThreads = await getGeneralMessageThreads(20, this.generaltMessageKeyword)
    
    return getValidArray(generalMessageThreads).filter(generalThread => {
      const userSeen = generalThread?.groupMessageThreadUserSeens?.find(
        seenData => seenData?.userId === this.rootStore.authStore.userDetail.id
      )
      const isSeen = userSeen?.lastSeenAt ? userSeen?.lastSeenAt >= generalThread?.lastMessageAt : false
      return !isSeen
    }).length
  }

  public async setCurrentThreadId(threadId: number): Promise<void> {
    this.currentThreadId = threadId
  }

  public async getMessages() {
    this.messageGroupThreads = this.messageGroupThreads.map(groupThread => {
      if (groupThread?.thread.id === this.currentGeneralThreadId) {
        groupThread.isSeen = true
        return groupThread
      }
      return groupThread
    })

    const filter = {
      order: 'createdAt ASC',
      include: [{ relation: 'user' }, { relation: 'receiver' }]
    }
    this.generalMessages = await getMessages(this.currentGeneralThreadId, filter)
    this.getUnreadThread()
  }

  // INFO: Support message
  public async getSupportMessageThreads(
    limit: number,
    isFetchFirstThread = false
  ): Promise<void> {
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0
    const pipeline = getSupportMessageThread(
      organizationId,
      this.sort,
      this.statusList,
      this.publish,
      this.supportMessageKeyword,
      limit,
      this?.rootStore?.authStore?.userDetail?.id ?? 0,
      this.rootStore.authStore.currentMembership?.roleId === AuthRoleIdEnum.PLAYER &&
        !this?.rootStore?.authStore?.userDetail?.isMessageFullAccess,
      this.isUnclaimed,
      this.isClaimedByMe,
      this.isClaimedByOthers,
      this.isResolved,
      this.includeParticipants
    )
    const [startDate, endDate] = this.modifiedDate
    this.supportThreads = await getSupportMessageAggregate(pipeline, startDate, endDate)
    if (this.currentSupportThreadId === 0 && this.supportThreads?.length > 0 && isFetchFirstThread) {
      this.currentSupportThreadId = this.supportThreads[0]?.thread?.id
    }
  }

  public async getTotalSupportMessageThreads(): Promise<void> {
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0
    const pipeline = getSupportMessageThread(
      organizationId,
      this.sort,
      this.statusList,
      this.publish,
      this.supportMessageKeyword,
      0,
      this?.rootStore?.authStore?.userDetail?.id ?? 0,
      this.rootStore.authStore.currentMembership?.roleId === AuthRoleIdEnum.PLAYER &&
        !this?.rootStore?.authStore?.userDetail?.isMessageFullAccess,
      this.isUnclaimed,
      this.isClaimedByMe,
      this.isClaimedByOthers,
      this.isResolved,
      this.includeParticipants,
      true
    )

    const [startDate, endDate] = this.modifiedDate
    const supportThreads = await getSupportMessageAggregate(pipeline, startDate, endDate)
    this.totalSupportThreadCount = getValidArray(supportThreads).length
  }

  public async getTotalSupportThreads(keyword: string): Promise<void> {
    const organizationId = this.rootStore.authStore.activeOrgId ?? 0
    const where = {
      organizationId,
      name: {
        like: `.*${keyword}.*`,
        options: 'i'
      },
      status: this.supportThreadFilter.status === 0 ? undefined : this.supportThreadFilter.status
    }
    this.totalSupportThreadCount = await getCountSupportMessageThreads(where)
  }

  public async setCurrentSupportThreadId(threadId: number): Promise<void> {
    this.currentSupportThreadId = threadId
  }

  public async resetSelectedThread(): Promise<void> {
    this.currentSupportThreadId = 0
    this.selectedSupportThread = defaultThead
  }

  public async getStepOptions(pipeline: AggregationPipeline): Promise<void> {
    this.stepOptions = await getStepsByAggregate(pipeline)
  }

  public async setSupportThreadFilter(
    status: SupportMessageThreadStatus,
    publish: ProcessPublishStatus
  ): Promise<void> {
    // INFO: Open later
    // this.supportThreadFilter = { status, publish }
  }

  public async getSupportMessages() {
    if (this.currentSupportThreadId === 0) {
      return
    }
    this.supportThreads = this.supportThreads.map(supportThread => {
      if (supportThread?.thread.id === this.currentSupportThreadId) {
        supportThread.isSeen = true
        return supportThread
      }
      return supportThread
    })
    const filter = {
      order: 'createdAt ASC',
      include: [
        {
          relation: 'user'
        }
      ]
    }
    this.supportMessages = await getSupportMessages(this.currentSupportThreadId, filter)
    this.getUnreadThread()
  }

  public async findAssignmentSupportThread(
    assignmentId: number,
    assignmentPlayerId: number,
    organizationId: number
  ): Promise<ISupportMessageThread | null> {
    const supportThreads = (await getSupportMessageThreads({
      where: {
        trainingAssignmentId: assignmentId,
        assignmentPlayerId,
        organizationId
      },
      order: ['lastMessageAt DESC'],
      limit: 1
    })) as ISupportThreadDto[]

    return supportThreads[0]?.thread ?? null
  }

  public async getAssignmentSupportThreadState(
    assignmentId: number,
    assignmentPlayerId: number,
    organizationId: number
  ): Promise<{ threadId: number | null; isSeen: boolean; unreadCount: number }> {
    const supportThreads = (await getSupportMessageThreads({
      where: {
        trainingAssignmentId: assignmentId,
        assignmentPlayerId,
        organizationId
      },
      order: ['lastMessageAt DESC'],
      limit: 1
    })) as ISupportThreadDto[]

    const first = supportThreads[0]
    const threadId = first?.thread?.id ?? null
    if (!threadId) {
      return { threadId: null, isSeen: true, unreadCount: 0 }
    }
    const isSeen = first?.isSeen ?? true

    return {
      threadId,
      isSeen,
      unreadCount: isSeen ? 0 : 1
    }
  }

  public async getSupportMessagesByThreadId(threadId: number): Promise<ISupportMessage[]> {
    return getSupportMessages(threadId, {
      order: 'createdAt ASC',
      include: [{ relation: 'user' }]
    })
  }

  public async sendAssignmentSupportMessage(params: {
    assignmentId: number
    assignmentPlayerId: number
    organizationId: number
    userId: number
    content: string
    attachments?: { name: string; type: S3FileTypeEnum }[]
    threadId?: number | null
  }): Promise<number | null> {
    const { assignmentId, assignmentPlayerId, organizationId, userId, content, attachments, threadId } = params
    if (threadId) {
      await createMessageInSupportThreads(threadId, { content, attachments })
      return threadId
    }

    const createdThread = await createSupportMessageThreads({
      trainingAssignmentId: assignmentId,
      assignmentPlayerId,
      organizationId,
      userId,
      content,
      attachments
    })
    return createdThread?.id ?? null
  }

  public async getThreadDetail() {
    const filter = {
      include: [
        {
          relation: 'users'
        },
        {
          relation: 'step',
          scope: {
            include: [
              {
                relation: 'process'
              }
            ]
          }
        },
        {
          relation: 'process'
        },
        {
          relation: 'trainingAssignment'
        },
        {
          relation: 'assignmentPlayer'
        }
      ]
    }
    if (this.currentSupportThreadId) {
      this.selectedSupportThread = await getSupportThread(this.currentSupportThreadId, filter)
    }
  }

  public async getStatusHistory(threadId: number): Promise<void> {
    this.statusHistory = await getSupportMessageThreadStatusHistory(threadId)
  }

  public changeSortBy(sortBy: string) {
    this.sort = sortBy as ESupportThreadSortBy
  }

  public changeStatus() {
    if (this.isUnclaimed && !this.statusList.includes(SupportMessageThreadStatus.UNCLAIMED)) {
      this.statusList.push(SupportMessageThreadStatus.UNCLAIMED)
    }
    if (!this.isUnclaimed && this.statusList.includes(SupportMessageThreadStatus.UNCLAIMED)) {
      this.statusList = this.statusList.filter(status => status !== SupportMessageThreadStatus.UNCLAIMED)
    }
    if (this.isResolved && !this.statusList.includes(SupportMessageThreadStatus.RESOLVED)) {
      this.statusList.push(SupportMessageThreadStatus.RESOLVED)
    }
    if (!this.isResolved && this.statusList.includes(SupportMessageThreadStatus.RESOLVED)) {
      this.statusList = this.statusList.filter(status => status !== SupportMessageThreadStatus.RESOLVED)
    }
  }

  public changeProcessType() {
    if (this.isPublished && !this.isDraft) {
      this.publish = ProcessPublishStatus.PUBLISHED
    } else if (!this.isPublished && this.isDraft) {
      this.publish = ProcessPublishStatus.DRAFT
    } else {
      this.publish = ProcessPublishStatus.ALL
    }
  }

  public setIsUnclaimed(isUnclaimed: boolean) {
    this.isUnclaimed = isUnclaimed
  }

  public setIsClaimedByMe(isClaimedByMe: boolean) {
    this.isClaimedByMe = isClaimedByMe
  }

  public setIsClaimedByOthers(isClaimedByOthers: boolean) {
    this.isClaimedByOthers = isClaimedByOthers
  }

  public setIsResolved(isResolved: boolean) {
    this.isResolved = isResolved
  }

  public setIsPublished(isPublished: boolean) {
    this.isPublished = isPublished
  }

  public setIsDraft(isDraft: boolean) {
    this.isDraft = isDraft
  }

  public setIncludeParticipants(participants: IUserWithRelations[]) {
    this.includeParticipants = participants
  }

  public setModifiedDate(modifiedDate: Date[]) {
    this.modifiedDate = modifiedDate
  }

  public updateUnreadGroupThread() {
    this.unreadGroupThreadCount -= 1
  }

  public updateUnreadSupportThread() {
    this.unreadSupportThreadCount -= 1
  }

  public updateSupportMessagePage(page: number) {
    this.supportMessagePage = page
  }

  public updateGeneralMessagePage(page: number) {
    this.generalMessagePage = page
  }

  public updateGeneralMessageKeyword(keyword: string) {
    this.generaltMessageKeyword = keyword
  }

  public updateSupportMessageKeyword(keyword: string) {
    this.supportMessageKeyword = keyword
  }

  public async getAllUnreadMessages(): Promise<{
    unreadGeneralMessages: IMessageGroupThreads[],
    unreadSupportMessages: ISupportThreadDto[]
  }> {
    const supportPipeline = getSupportMessageThreadForUnreadCount(
      this.rootStore.authStore.activeOrgId ?? 0,
      this.rootStore.authStore.userDetail.id,
      this.rootStore.authStore.currentMembership?.roleId === AuthRoleIdEnum.PLAYER &&
        !this?.rootStore?.authStore?.userDetail?.isMessageFullAccess
    )
    
    const [unreadSupportMessages, unreadGeneralMessages] = await Promise.all([
      getAllUnreadSupportMessages(supportPipeline),
      getAllUnreadGeneralMessages()
    ])
    
    return {
      unreadGeneralMessages,
      unreadSupportMessages
    }
  }
}

export default MessageStore
